import { FORM_ERROR } from "final-form";
import formatString from "format-string-by-pattern";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { Form, FormSpy } from "react-final-form";
import { connect } from "react-redux";
import { useHistory, useParams } from "react-router";
import { Col, Container, Row, Spinner, Toast } from "reactstrap";
import AppBar from "src/components/AppBar/AppBar";
import { Container as ProspectContainer } from "src/components/Container/Container";
import SimpleDatePickerField from "src/components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import SingleSelectField from "src/components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "src/components/Form/TextFieldV2/TextFieldV2";
import { IconName } from "src/components/Icon/Icon";
import { ProspectsType } from "src/store/models/prospects.model";
import { createProspect, fetchProspect, updateProspect } from "src/store/reducers/prospects";
import { fetchProspectsOptions } from "src/store/reducers/prospectsBuilder";
import { ResourceStore } from "src/store/reducers/resource";
import { addDefaultOption } from "src/util/utils.defaultValues";
import * as S from "../../constants/StringConstants";
import { IRootState } from "../../store/reducers";

export type DropdownOptionType = {
  key: string;
  value: number;
};

export type DropdownOptionsType = {
  loading: boolean;
  selectStateList: DropdownOptionType[];
};

type IProspectsBuilderProps = {
  dropdownOptions: DropdownOptionsType;
  prospects: ResourceStore<ProspectsType>;
  loading: boolean;
  fetchProspect: (id: string) => any;
  updateProspect: (prospect: ProspectsType) => void;
  createProspect: (prospect: ProspectsType) => void;
  fetchProspectsOptions: () => void;
};

const ProspectsBuilder: React.FC<IProspectsBuilderProps> = ({
  dropdownOptions,
  fetchProspectsOptions,
  fetchProspect,
  loading,
}) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [initialState, setInitialState] = useState({});
  const isNew = id === "new";

  const phoneMask = { name: "phone-1", parse: "(999) 999-9999.999999" };
  const stateList = addDefaultOption("", get(dropdownOptions, "selectStateList", []));

  useEffect(() => {
    fetchProspectsOptions();
  }, [fetchProspectsOptions]);

  useEffect(() => {
    fetchProspectData();
  }, [id, fetchProspect]);

  const validate = (values: ProspectsType) => {
    const errors: Partial<ProspectsType> = {};
    if (!values.firstName) {
      errors["firstName"] = S.FORM_FIELD_REQUIRED;
    }
    if (!values.lastName) {
      errors["lastName"] = S.FORM_FIELD_REQUIRED;
    }
    return errors;
  };

  const handleSubmit = async (values: ProspectsType) => {
    try {
      if (isNew) {
        await createProspect(values);
      } else {
        await updateProspect(values);
      }
      history.push("/prospects/all");
    } catch (error) {
      return { [FORM_ERROR]: "Error Submitting" };
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  async function fetchProspectData() {
    try {
      if (id && !isNew) {
        const response = await fetchProspect(id);
        setInitialState(response.value.data);
      }
    } catch (error) {
      console.error("error fetching prospect data: ", error);
    }
  }

  return (
    <>
      <Form
        validate={validate}
        onSubmit={handleSubmit}
        initialValues={initialState}
        render={({ handleSubmit, submitting }) => (
          <>
            <AppBar icon={IconName.Prospects} title={S.PROSPECTS_PAGE_TITLE} />

            <form onSubmit={handleSubmit}>
              <div className="broker-builder__form">
                <section className="broker-builder__wrap">
                  <ProspectContainer>
                    <div className="broker-builder__detail-form">
                      <Col className="app-form-header">
                        <h4 className="form-title">{S.BRO_INFO_TITLE}</h4>
                      </Col>

                      <Row>
                        <Col>
                          <TextField
                            name="firstName"
                            key="prospect.firstName-key"
                            label={S.PROSPECTS_BUILDER.FIRST_NAME}
                          />
                        </Col>
                        <Col>
                          <TextField
                            name="middleName"
                            key="prospect.middleName-key"
                            label={S.PROSPECTS_BUILDER.MIDDLE_NAME}
                          />
                        </Col>
                        <Col>
                          <TextField
                            name="lastName"
                            key="prospect.lastName-key"
                            label={S.PROSPECTS_BUILDER.LAST_NAME}
                          />
                        </Col>
                        <Col sm="3">
                          <TextField
                            name="preferredName"
                            key="prospect.preferredName-key"
                            label={S.PROSPECTS_BUILDER.PREFERRED_NAME}
                          />
                        </Col>
                        <Col>
                          <TextField name="email" key="prospect.email-key" label={S.PROSPECTS_BUILDER.EMAIL} />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm="3">
                          <SimpleDatePickerField name="dob" label={S.BRO_DOB_LABEL} isFormMode />
                        </Col>
                        <Col>
                          <FormSpy
                            render={() => (
                              <Row>
                                <Col sm="4" className="broker-builder__phone-radio">
                                  <div className="broker-builder__利物short-div">
                                    <TextField
                                      name="phone"
                                      key="prospect.phone-key"
                                      label={S.BRO_PHONE_TYPE_PHONE}
                                      type="tel"
                                      parse={formatString(phoneMask.parse)}
                                    />
                                  </div>
                                </Col>
                              </Row>
                            )}
                          />
                        </Col>
                      </Row>
                      <Col className="app-form-header">
                        <h4 className="form-title">{S.BRO_ADDRESS_TITLE}</h4>
                      </Col>
                      <Row>
                        <Col sm="3">
                          <TextField
                            name="address"
                            key="prospect.address-key"
                            label={S.BRO_STREET_ADDRESS_LABEL}
                            multiline
                          />
                        </Col>
                        <Col sm="3">
                          <TextField name="city" key="prospect.city-key" label={S.BRO_CITY_LABEL} />
                        </Col>
                        <Col sm="6">
                          <Container className="bootsrap-container-override">
                            <Row>
                              <Col>
                                <SingleSelectField
                                  name="state"
                                  key="prospect.state-key"
                                  label={S.BRO_STATE_LABEL}
                                  options={stateList}
                                />
                              </Col>
                              <Col>
                                <TextField name="zip" key="prospect.zip-key" label={S.BRO_ZIP_LABEL} />
                              </Col>
                            </Row>
                          </Container>
                        </Col>
                      </Row>
                      <hr />
                      <Row>
                        <Col className="app-form-header">
                          <h4 className="form-title">{S.BRO_NOTES_TITLE}</h4>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm="6">
                          <TextField name="notes" key="broker.notes-key" label={S.BRO_NOTES_LABEL} multiline />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div className="broker-builder__top-bar">
                            <div className="buttons">
                              <button className="button-v2 button-v2__secondary" type="button" onClick={handleCancel}>
                                {S.BRO_CANCEL_BUTTON}
                              </button>

                              <button className="button-v2 button-v2__primary" type="submit" disabled={submitting}>
                                {submitting ? <Spinner color="light" /> : S.BRO_SUBMIT_BUTTON}
                              </button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </ProspectContainer>
                </section>
              </div>
            </form>
          </>
        )}
      />
      <Toast message={S.LOADING} onClose={() => {}} open={loading} />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  dropdownOptions: state.prospectsBuilder,
  prospects: state.prospects,
  loading: state.prospects.loading,
});

const mapDispatchToProps = {
  fetchProspect,
  createProspect,
  updateProspect,
  fetchProspectsOptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProspectsBuilder);
